import { Link, router } from '@inertiajs/react'
import { useEffect, useState } from 'react'
import InfoTip from '../../components/InfoTip'

// ─── Milestone config (mirrors OfferVuu MILESTONE_CONFIG) ─────────────────────
const MILESTONE = {
  queue_activated:   { label: 'Active',           color: '#2196F3' },
  order_submitted:   { label: 'Order In',          color: '#9C27B0' },
  preparing:         { label: 'Preparing',         color: '#FF9800' },
  ready:             { label: 'Ready',             color: '#4CAF50' },
  guest_eta_update:  { label: 'En Route',          color: '#2196F3' },
  guest_arrived:     { label: 'Arrived',           color: '#FFC107' },
  checkin_verified:  { label: 'Verified',          color: '#009688' },
  escrow_released:   { label: 'Released',          color: '#4CAF50' },
  session_closed:    { label: 'Closed',            color: '#607D8B' },
  sensepoint_request:{ label: 'Arrival Request',   color: '#FF9800' },
  sensepoint_ready:  { label: 'SP Ready',           color: '#009688' },
  handoff_complete:  { label: 'Delivered',         color: '#4CAF50' },
  mode_changed:      { label: 'Mode Changed',      color: '#607D8B' },
  // session_status fallbacks
  active:            { label: 'Active',            color: '#2196F3' },
  awaiting_sensepoint:{ label: 'Awaiting SP',     color: '#FF9800' },
  delivered:         { label: 'Delivered',         color: '#4CAF50' },
  settled:           { label: 'Settled',           color: '#4CAF50' },
  cancelled:         { label: 'Cancelled',         color: '#F44336' },
  closed:            { label: 'Closed',            color: '#607D8B' },
}

function milestoneBadge(key) {
  const m = MILESTONE[key] || { label: key?.replace(/_/g, ' ') || '—', color: '#9E9E9E' }
  return (
    <span
      style={{ backgroundColor: m.color + '22', color: m.color, borderColor: m.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold"
    >
      <span style={{ backgroundColor: m.color }} className="w-2 h-2 rounded-full inline-block" />
      {m.label}
    </span>
  )
}

// Connection health — thresholds mirror HeartbeatMonitorJob constants
function connectionHealth(lastSeenIso) {
  if (!lastSeenIso) return { label: '—', color: '#9E9E9E', dot: 'bg-gray-300' }
  const gapSec = (Date.now() - new Date(lastSeenIso).getTime()) / 1000
  if (gapSec < 20)  return { label: `${Math.round(gapSec)}s ago`,          color: '#4CAF50', dot: 'bg-green-500' }
  if (gapSec < 180) return { label: `${Math.round(gapSec / 60)}m ago`,      color: '#FF9800', dot: 'bg-yellow-500' }
  if (gapSec < 300) return { label: `${Math.round(gapSec / 60)}m ago ⚠`,   color: '#FF5722', dot: 'bg-orange-500' }
  return               { label: `${Math.round(gapSec / 60)}m ago ✕`,       color: '#F44336', dot: 'bg-red-500' }
}

function ConnectionCell({ lastSeenIso, userId }) {
  const h = connectionHealth(lastSeenIso)
  return (
    <div>
      <p className="text-xs font-mono text-gray-500">{userId ? `u${userId}` : '—'}</p>
      <span className="flex items-center gap-1 mt-0.5">
        <span className={`w-2 h-2 rounded-full flex-shrink-0 ${h.dot}`} />
        <span className="text-xs" style={{ color: h.color }}>{h.label}</span>
      </span>
    </div>
  )
}

// ─── Escrow badge ──────────────────────────────────────────────────────────────
const ESCROW_META = {
  pending:             { label: 'Pending',      color: '#9E9E9E' },
  held:                { label: 'Held',         color: '#FF9800' },
  attestation_pending: { label: 'Attest',       color: '#2196F3' },
  released:            { label: 'Released',     color: '#4CAF50' },
  voided:              { label: 'Voided',       color: '#F44336' },
}

function escrowBadge(status) {
  const m = ESCROW_META[status] || { label: status || '—', color: '#9E9E9E' }
  return (
    <span
      style={{ backgroundColor: m.color + '22', color: m.color, borderColor: m.color }}
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full border text-xs font-bold"
    >
      <span style={{ backgroundColor: m.color }} className="w-1.5 h-1.5 rounded-full inline-block" />
      {m.label}
    </span>
  )
}

function statusBadge(status) {
  const colors = {
    active:              'bg-blue-100 text-blue-800',
    awaiting_sensepoint: 'bg-yellow-100 text-yellow-800',
    paused:              'bg-orange-100 text-orange-800',
    delivered:           'bg-green-100 text-green-800',
    settled:             'bg-green-100 text-green-800',
    closed:              'bg-gray-100 text-gray-600',
    cancelled:           'bg-red-100 text-red-700',
  }
  return (
    <span className={`px-2 py-0.5 rounded text-xs font-semibold uppercase tracking-wide ${colors[status] || 'bg-gray-100 text-gray-600'}`}>
      {status?.replace(/_/g, ' ')}
    </span>
  )
}

export default function QueueMonitor({ sessions: initialSessions = [] }) {
  const [sessions, setSessions] = useState(initialSessions)
  const [lastRefresh, setLastRefresh] = useState(new Date())

  // Auto-refresh every 10s — full Inertia page reload preserving scroll
  useEffect(() => {
    const interval = setInterval(() => {
      router.reload({ only: ['sessions'], onSuccess: (page) => {
        setSessions(page.props.sessions)
        setLastRefresh(new Date())
      }})
    }, 10000)
    return () => clearInterval(interval)
  }, [])

  const activeCount = sessions.filter(s => ['active', 'awaiting_sensepoint', 'paused'].includes(s.session_status)).length
  const pausedCount = sessions.filter(s => s.session_status === 'paused').length

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs text-gray-400 mb-0.5">
              <Link href="/offers" className="text-gray-500 hover:text-gray-700">← Available Offers</Link>
            </p>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-1">
              Queue Monitor
              <InfoTip text="Live view of all QueueSession records. A session is created when a guest accepts an offer. Sessions flow: pending → active → awaiting_sensepoint → delivered → settled/cancelled. Auto-refreshes every 10s." position="right" />
            </h1>
            <p className="text-sm text-gray-500 mt-0.5">
              Live lifecycle view · {activeCount} open
              <InfoTip text="Open sessions: status is active, awaiting_sensepoint, or paused. Paused sessions have IROP or creator action pending." position="bottom" />
              {pausedCount > 0 ? ` (${pausedCount} paused)` : ''} · refreshes every 10s
              {' · '}
              <Link href="/admin/queue_monitor/history" className="text-blue-500 hover:underline">
                History →
              </Link>
              {' · '}
              <Link href="/admin/network_monitor" className="text-blue-500 hover:underline">Network Monitor</Link>
              {' · '}
              <Link href="/admin/journey_monitor" className="text-blue-500 hover:underline">Journey Monitor</Link>
            </p>
          </div>
          <div className="text-right">
            <p className="text-xs text-gray-400">Last refresh</p>
            <p className="text-sm font-mono text-gray-600">
              {lastRefresh.toLocaleTimeString()}
            </p>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 py-6">
        {sessions.length === 0 ? (
          <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
            <p className="text-4xl mb-3">🗂</p>
            <p className="text-xl font-semibold text-gray-700">No active queues</p>
            <p className="text-gray-500 mt-1">Queue sessions will appear here when guests accept offers.</p>
            <Link href="/offers" className="inline-block mt-4 text-sm text-blue-500 hover:underline">View Available Offers →</Link>
          </div>
        ) : (
          <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-gray-50 border-b border-gray-200 text-left text-xs text-gray-500 uppercase tracking-wider">
                  <th className="px-4 py-3 font-semibold">Session</th>
                  <th className="px-4 py-3 font-semibold">Offer</th>
                  <th className="px-4 py-3 font-semibold">Status</th>
                  <th className="px-4 py-3 font-semibold">Escrow</th>
                  <th className="px-4 py-3 font-semibold">Milestone</th>
                  <th className="px-4 py-3 font-semibold">Mode</th>
                  <th className="px-4 py-3 font-semibold">Events</th>
                  <th className="px-4 py-3 font-semibold">Creator · Last Seen</th>
                  <th className="px-4 py-3 font-semibold">Guest · Last Seen</th>
                  <th className="px-4 py-3 font-semibold">Started</th>
                  <th className="px-4 py-3 font-semibold"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {sessions.map(s => (
                  <tr
                    key={s.id}
                    className={`hover:bg-blue-50 transition-colors cursor-pointer ${s.session_status === 'paused' ? 'bg-orange-50' : ''}`}
                    onClick={() => router.visit(`/admin/queue_monitor/${s.id}`)}
                  >
                    <td className="px-4 py-3 font-mono text-gray-700">#{s.id}</td>
                    <td className="px-4 py-3 max-w-[180px]">
                      <p className="font-medium text-gray-800 truncate">{s.offer_name}</p>
                      <Link
                        href={`/offers/${s.offer_id}`}
                        className="text-xs text-blue-500 hover:underline"
                        onClick={e => e.stopPropagation()}
                      >
                        Offer #{s.offer_id} →
                      </Link>
                    </td>
                    <td className="px-4 py-3">{statusBadge(s.session_status)}</td>
                    <td className="px-4 py-3">{escrowBadge(s.escrow_status)}</td>
                    <td className="px-4 py-3">{milestoneBadge(s.latest_event || s.session_status)}</td>
                    <td className="px-4 py-3">
                      <div className="flex flex-col gap-0.5">
                        <span className="text-xs font-mono text-gray-500 bg-gray-100 px-2 py-0.5 rounded">
                          {s.sensepoint_mode || 'cloud'}
                        </span>
                        {s.self_fulfillment && (
                          <span className="text-xs font-bold text-purple-700 bg-purple-100 px-2 py-0.5 rounded">
                            SELF
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-center">
                      <span className="text-gray-700 font-semibold">{s.event_count}</span>
                      {s.message_count > 0 && (
                        <span className="ml-1 text-xs text-gray-400">+{s.message_count}m</span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <ConnectionCell lastSeenIso={s.creator_last_seen_at} userId={s.creator_user_id} />
                    </td>
                    <td className="px-4 py-3">
                      <ConnectionCell lastSeenIso={s.guest_last_seen_at} userId={s.guest_user_id} />
                    </td>
                    <td className="px-4 py-3 text-xs text-gray-400 whitespace-nowrap">
                      {s.created_at ? new Date(s.created_at).toLocaleString(undefined, {
                        month: 'short', day: 'numeric',
                        hour: '2-digit', minute: '2-digit'
                      }) : '—'}
                    </td>
                    <td className="px-4 py-3">
                      <Link
                        href={`/admin/queue_monitor/${s.id}`}
                        className="text-blue-600 hover:underline text-xs"
                        onClick={e => e.stopPropagation()}
                      >
                        Detail →
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  )
}
